import { getInitialTravelerInfoContext } from "@apac/checkout";
import {
  type ActivityLanguageGuide,
  BookingQuestionId,
  type BookingQuestions,
} from "@b2bportal/rbc-activities-api";
import {
  type ActivityAdditionalDetailsContext,
  getCartInitialContext,
  getInitialCardPaymentContext,
  type IContactInfo,
  ParentState,
} from "@hopper-b2b/checkout";
import type { IPerson, PersonId, SessionInfo } from "@hopper-b2b/types";
import type { ActivitiesMachineContext } from "./types";
import {
  termsAndConditionsStep,
  getInitialRewardsPaymentContext,
} from "@/modules/checkout";

// We only handle these per booking questions in checkout
const HANDLED_BOOKING_QUESTIONS: BookingQuestionId[] = [
  BookingQuestionId.SpecialRequirements,
  BookingQuestionId.PickupPoint,
  BookingQuestionId.TransferArrivalMode,
  BookingQuestionId.TransferDepartureMode,
];

const getActivityInformationContext = (
  bookingQuestions?: BookingQuestions,
  languageGuides?: ActivityLanguageGuide[],
  participantCount?: number,
  isPickupIncluded?: boolean
): ActivityAdditionalDetailsContext => {
  const handledBookingQuestions = bookingQuestions?.perBooking?.filter(
    (question) => HANDLED_BOOKING_QUESTIONS.includes(question.id)
  );
  const hasAdditionalDetailsStep =
    handledBookingQuestions?.length > 0 || languageGuides?.length > 0;

  // Initialize selectedLangGuide to the first language guide if available
  const selectedLangGuide = languageGuides?.[0];

  return {
    hasAdditionalDetailsStep,
    bookingQuestions: handledBookingQuestions,
    selectedPickupPoint: undefined,
    hotelSelection: undefined,
    hotelName: undefined,
    address: undefined,
    city: undefined,
    zipCode: undefined,
    selectedLangGuide,
    specialRequirement: undefined,
    travelerBookingQuestions: bookingQuestions?.perTraveler,
    travelerBookingQuestionAnswers: [],
    participantCount,
    isPickupIncluded,
  };
};

interface InitialContextParams {
  bookingQuestions?: BookingQuestions;
  languageGuides?: ActivityLanguageGuide[];
  participantCount?: number;
  isPickupIncluded?: boolean;
}

export const getInitialActivitiesMachineContext = (
  params?: InitialContextParams
): ActivitiesMachineContext => ({
  ...getCartInitialContext(),
  sessionInfo: {} as SessionInfo,
  featureFlags: {},
  [ParentState.activitiesShop]: {
    activityId: undefined,
    opaqueShoppedProduct: undefined,
  },
  [ParentState.activityInformation]: getActivityInformationContext(
    params?.bookingQuestions,
    params?.languageGuides,
    params?.participantCount,
    params?.isPickupIncluded
  ),
  [ParentState.passengerInformation]: {
    selectedPassengerIds: [] as Array<PersonId>,
    userPassengers: [] as Array<IPerson>,
    numPassengerAlertDismissed: false,
    visited: false,
  },
  [ParentState.contactInformation]: {
    contactInfo: {
      countryCode: "",
      phoneNumber: "",
      email: "",
    } as IContactInfo,
    visited: false,
  },
  [ParentState.cardPayment]: getInitialCardPaymentContext(),
  [ParentState.review]: {
    visited: false,
    [termsAndConditionsStep]: {
      accepted: false,
    },
  },
  [ParentState.awardsPayment]: getInitialRewardsPaymentContext(),
  [ParentState.bookingConfirmation]: {},
  [ParentState.travelerInformation]: getInitialTravelerInfoContext(),
});
